/**
 * Runtime graphics quality tiers for post-FX / shadows / DPR.
 *
 * Override: localStorage.setItem('radivas_gfx_quality', 'low'|'medium'|'high'|'mobile')
 *
 * Foldables / phones with 8–12GB RAM often report deviceMemory=8 and get
 * desktop HIGH (DPR 2 + 4K shadows + god rays) — that thermal-throttles hard.
 * Touch / coarse pointer / Android UA force a mobile-safe cap.
 */

export const GFX_QUALITY = {

  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  MOBILE: 'mobile',

}

const TIER = {

  [ GFX_QUALITY.HIGH ]: {

    dprMax: 2,
    shadowMapSize: 4096,
    softShadows: true,
    softShadowSamples: 12,
    softShadowRings: 9,
    antialias: true,
    bloom: true,
    vignette: true,
    smaa: true,
    ssao: false,
    godRays: true,
    ssr: false,
    motes: true,
    colorGrade: true,
    powerPreference: 'high-performance',

  },

  [ GFX_QUALITY.MEDIUM ]: {

    dprMax: 1.5,
    shadowMapSize: 2048,
    softShadows: true,
    softShadowSamples: 8,
    softShadowRings: 7,
    antialias: true,
    bloom: true,
    vignette: true,
    smaa: true,
    ssao: false,
    godRays: false,
    ssr: false,
    motes: true,
    colorGrade: true,
    powerPreference: 'high-performance',

  },

  [ GFX_QUALITY.MOBILE ]: {

    // Fold 4 inner screen is huge — keep pixel fill modest
    dprMax: 1.25,
    shadowMapSize: 1024,
    softShadows: false,
    softShadowSamples: 4,
    softShadowRings: 5,
    antialias: false,
    bloom: false,
    vignette: true,
    smaa: false,
    ssao: false,
    godRays: false,
    ssr: false,
    motes: false,
    colorGrade: true,
    powerPreference: 'default',

  },

  [ GFX_QUALITY.LOW ]: {

    dprMax: 1,
    shadowMapSize: 512,
    softShadows: false,
    softShadowSamples: 4,
    softShadowRings: 5,
    antialias: false,
    bloom: false,
    vignette: true,
    smaa: false,
    ssao: false,
    godRays: false,
    ssr: false,
    motes: false,
    colorGrade: false,
    powerPreference: 'low-power',

  },

}

const TIER_ORDER = [

  GFX_QUALITY.LOW,
  GFX_QUALITY.MOBILE,
  GFX_QUALITY.MEDIUM,
  GFX_QUALITY.HIGH,

]

export function isMobileClient() {

  if ( typeof window === 'undefined' || typeof navigator === 'undefined' ) return false;

  try {

    const ua = navigator.userAgent || '';
    if ( /Android|iPhone|iPad|iPod|Mobile|SamsungBrowser/i.test( ua ) ) return true;

    const coarse = window.matchMedia?.( '(pointer: coarse)' )?.matches;
    const noHover = window.matchMedia?.( '(hover: none)' )?.matches;
    const touchPoints = navigator.maxTouchPoints || 0;

    // Fold / tablets: coarse pointer + touch, even if UA is "desktop-ish"
    if ( coarse && touchPoints > 1 ) return true;
    if ( noHover && touchPoints > 1 && ( window.screen?.width || 0 ) < 1400 ) return true;

  } catch ( e ) { /* ignore */ }

  return false;

}

function detectTier() {

  if ( typeof window === 'undefined' ) return GFX_QUALITY.MEDIUM;

  try {

    const forced = window.localStorage?.getItem( 'radivas_gfx_quality' );
    if ( forced && TIER[ forced ] ) return forced;

  } catch ( e ) { /* private mode */ }

  const mobile = isMobileClient();
  const mem = navigator.deviceMemory;
  const cores = navigator.hardwareConcurrency || 4;

  if ( mobile ) {

    // Flagships (Fold 4 etc.) still need mobile budget — not desktop HIGH
    if ( mem && mem <= 3 ) return GFX_QUALITY.LOW;
    return GFX_QUALITY.MOBILE;

  }

  if ( mem && mem <= 2 ) return GFX_QUALITY.LOW;
  if ( mem && mem <= 4 ) return GFX_QUALITY.MEDIUM;
  if ( cores <= 4 && ( !mem || mem <= 4 ) ) return GFX_QUALITY.MEDIUM;

  return GFX_QUALITY.HIGH;

}

let cached = null;

export function getGfxQuality() {

  if ( !cached ) {

    const id = detectTier();
    cached = { id, mobile: isMobileClient(), ...TIER[ id ] };

  }

  return cached;

}

/** Force a lower/higher tier at runtime (adaptive FPS). */
export function setGfxQuality( id ) {

  if ( !TIER[ id ] ) return getGfxQuality();

  cached = { id, mobile: isMobileClient(), ...TIER[ id ] };
  return cached;

}

export function tierBelow( id ) {

  const i = TIER_ORDER.indexOf( id );
  if ( i <= 0 ) return TIER_ORDER[ 0 ];
  return TIER_ORDER[ i - 1 ];

}

export function applyGfxToPostfx( postfx ) {

  const q = getGfxQuality();
  if ( !postfx ) return postfx;

  return {

    ...postfx,
    enabled: postfx.enabled !== false,
    colorGrade: q.colorGrade !== false,
    ssao: {

      ...( postfx.ssao || {} ),
      enabled: !!( postfx.ssao?.enabled && q.ssao ),

    },
    bloom: {

      ...( postfx.bloom || {} ),
      intensity: q.bloom ? ( postfx.bloom?.intensity ?? 0.22 ) : 0,

    },
    vignette: q.vignette ? postfx.vignette : null,
    smaa: q.smaa,
    godRays: q.godRays,
    ssr: q.ssr,

  };

}
